import { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { t } from 'i18next';
import { ShowGptSummaryView } from './ShowGptSummaryView';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useNotification } from '../../Contexts/NotificationContext';
import { 
    Conversation, 
    IGptConfiguration 
} from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useGptConfigurations } from '../../store/hooks';
import { addConversationAction } from '../../store/actions/conversationActions';
import { buildRoute, ChatRoute, PersonalGptSpecificRoute } from '../../Routes';
import { useUserContext } from '../../Contexts/UserContext';

const ShowGptSummaryHandler: FC = () => {
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const { gptConfigurations } = useGptConfigurations();
    const { chats, dispatch: conversationDispatch } = useConversations();
    const { userProfile } = useUserContext();
    const { id } = useParams<{ id: string }>();

    // Find the GPT by ID from the route params
    const selectedGpt = gptConfigurations.find(g => g.id === id) || null;

    const onConfigure = useCallback((gpt: IGptConfiguration) => {
        if (gpt.id) {
            navigate(buildRoute(PersonalGptSpecificRoute, { id: gpt.id }));
        }
    }, [navigate]);

    const onStartDiscussion = useCallback((gpt: IGptConfiguration) => {
        performEnterpriseGPTApiCall(async () => {
            try {
                // If the GPT is default (SyGPT), check for existing draft conversation and navigate to it
                if (gpt.isDefault) {
                    const draftConversation = chats.find(c => c.id === 'DRAFT');
                    if (draftConversation) {
                        navigate(ChatRoute);
                        return;
                    }
                }

                // Create a draft conversation locally
                const draftConversation = new Conversation({
                    id: 'DRAFT',
                    gpt: { id: gpt?.id, type: gpt?.type },
                    prompts: [],
                    title: '',
                    currentQuery: '',
                    startedOn: new Date(),
                    userName: userProfile?.username || '',
                    llm: ''
                });

                conversationDispatch(addConversationAction({ ...draftConversation, draft: true }));
                navigate(ChatRoute);
            } catch (e) {
                dispatchNotification('error.api.post.conversation', 'error');
            }
        });
    }, [chats, conversationDispatch, dispatchNotification, navigate, userProfile]);

    const onDiscussionClick = useCallback((_discussionId: string) => {
        // TODO: Navigate to the specific discussion/conversation
        // For now, just navigate to chat route
        navigate(ChatRoute);
        dispatchNotification(t('exploreGpt.discussionOpened', 'Opening discussion...'), 'info');
    }, [navigate, dispatchNotification]);

    return (
        <ShowGptSummaryView
            gpt={selectedGpt}
            onConfigure={onConfigure}
            onStartDiscussion={onStartDiscussion}
            onDiscussionClick={onDiscussionClick}
        />
    );
};

export default ShowGptSummaryHandler;
